import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { US_STATES, VEHICLE_BRANDS, TRANSMISSIONS, FUEL_TYPES, CONDITIONS } from "@/lib/listings";
import { Plus, Search, Filter, X, MapPin, Calendar, Gauge, Fuel, Settings } from "lucide-react";

const VehicleCard = ({ v, lang, t, onClick }) => (
  <button
    onClick={onClick}
    className="text-right group glass-card rounded-2xl overflow-hidden hover:border-yellow-500/60 transition-all"
    data-testid={`vehicle-card-${v.vehicle_id}`}
  >
    <div className="relative h-52 overflow-hidden bg-slate-900">
      <ListingImage path={v.images?.[0]} alt={v.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
      <Badge className={`absolute top-3 right-3 ${v.listing_type === "rent" ? "bg-blue-500" : "bg-yellow-500"} text-black`}>
        {v.listing_type === "rent" ? t("للإيجار", "For Rent") : t("للبيع", "For Sale")}
      </Badge>
      <div className="absolute bottom-3 right-3 text-yellow-400 font-bold text-lg">
        ${v.price?.toLocaleString()}{v.listing_type === "rent" && <span className="text-sm text-slate-300">/{t("يوم", "day")}</span>}
      </div>
    </div>
    <div className="p-4 space-y-2">
      <h3 className="font-bold text-white truncate">{v.brand} {v.model} {v.year}</h3>
      <div className="flex items-center gap-1 text-sm text-slate-400">
        <MapPin size={14} className="text-yellow-500" />
        <span className="truncate">{v.city}, {v.state}</span>
      </div>
      <div className="flex items-center gap-3 text-sm text-slate-300 pt-1">
        <span className="flex items-center gap-1"><Gauge size={14} /> {v.mileage?.toLocaleString()}</span>
        <span className="flex items-center gap-1"><Settings size={14} /> {v.transmission?.slice(0, 4)}</span>
        <span className="flex items-center gap-1"><Fuel size={14} /> {v.fuel_type?.slice(0, 3)}</span>
      </div>
    </div>
  </button>
);

export default function VehicleMarketplace() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    listing_type: "", state: "", city: "", brand: "",
    min_price: "", max_price: "", min_year: "", transmission: "", fuel_type: "", condition: ""
  });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== null));
      const res = await axios.get(`${API}/vehicles`, { params });
      setVehicles(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const apply = () => { load(filters); setShowFilters(false); };
  const reset = () => {
    const empty = { listing_type: "", state: "", city: "", brand: "", min_price: "", max_price: "", min_year: "", transmission: "", fuel_type: "", condition: "" };
    setFilters(empty); load(empty);
  };

  return (
    <DashboardLayout title={t("سوق السيارات", "Vehicle Marketplace")}>
      <div className="space-y-6" data-testid="vehicle-marketplace">
        {/* Hero */}
        <div className="relative glass-card rounded-3xl p-6 lg:p-10 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/15 blur-[120px] rounded-full" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-yellow-500/15 blur-[120px] rounded-full" />
          <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Badge className="bg-blue-500/20 text-blue-400 mb-3">{t("سوق السيارات", "Cars Marketplace")}</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">{t("سيارات للبيع والإيجار", "Cars for sale & rent")}</h2>
              <p className="text-slate-400">{t("ابحث عن سيارتك المثالية بأفضل الأسعار من المجتمع العربي", "Find your perfect car at the best prices from the Arab community")}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate("/vehicles/mine")} variant="outline" className="border-slate-700 text-white" data-testid="my-vehicles-btn">
                {t("سياراتي", "My Cars")}
              </Button>
              <Button onClick={() => navigate("/vehicles/post")} className="btn-primary" data-testid="post-vehicle-btn">
                <Plus className="ml-2" size={18} /> {t("نشر سيارة", "Post Vehicle")}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs Sale/Rent */}
        <div className="flex gap-2">
          {[
            { v: "", label: t("الكل", "All") },
            { v: "sale", label: t("للبيع", "For Sale") },
            { v: "rent", label: t("للإيجار", "For Rent") }
          ].map((tab) => (
            <button
              key={tab.v || "all"}
              onClick={() => { const next = { ...filters, listing_type: tab.v }; setFilters(next); load(next); }}
              className={`px-5 py-2 rounded-full transition-colors ${filters.listing_type === tab.v ? "bg-yellow-500 text-black" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`}
              data-testid={`vehicle-tab-${tab.v || "all"}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="glass-card rounded-2xl p-4 flex flex-col md:flex-row gap-3">
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
            <select value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="filter-brand">
              <option value="">{t("كل الماركات", "All Brands")}</option>
              {VEHICLE_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="vfilter-state">
              <option value="">{t("كل الولايات", "All States")}</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
            </select>
            <input placeholder={t("المدينة", "City")} value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="vfilter-city" />
            <input type="number" placeholder={t("من سنة", "Min year")} value={filters.min_year}
              onChange={(e) => setFilters({ ...filters, min_year: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="vfilter-year" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowFilters(true)} variant="outline" className="border-slate-700 text-white" data-testid="vmore-filters-btn">
              <Filter size={16} className="ml-2" /> {t("المزيد", "More")}
            </Button>
            <Button onClick={apply} className="btn-primary" data-testid="vsearch-btn">
              <Search size={16} className="ml-2" /> {t("بحث", "Search")}
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="glass-card rounded-2xl p-5 space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-bold text-white">{t("فلاتر متقدمة", "Advanced filters")}</h4>
              <button onClick={() => setShowFilters(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <input type="number" placeholder={t("أقل سعر", "Min price")} value={filters.min_price}
                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" />
              <input type="number" placeholder={t("أعلى سعر", "Max price")} value={filters.max_price}
                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" />
              <select value={filters.transmission} onChange={(e) => setFilters({ ...filters, transmission: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white">
                <option value="">{t("ناقل الحركة", "Transmission")}</option>
                {TRANSMISSIONS.map((tr) => <option key={tr.value} value={tr.value}>{language === "ar" ? tr.label_ar : tr.label_en}</option>)}
              </select>
              <select value={filters.fuel_type} onChange={(e) => setFilters({ ...filters, fuel_type: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white">
                <option value="">{t("الوقود", "Fuel")}</option>
                {FUEL_TYPES.map((f) => <option key={f.value} value={f.value}>{language === "ar" ? f.label_ar : f.label_en}</option>)}
              </select>
              <select value={filters.condition} onChange={(e) => setFilters({ ...filters, condition: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white">
                <option value="">{t("الحالة", "Condition")}</option>
                {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{language === "ar" ? c.label_ar : c.label_en}</option>)}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button onClick={reset} variant="ghost" className="text-slate-300">{t("إعادة ضبط", "Reset")}</Button>
              <Button onClick={apply} className="btn-primary">{t("تطبيق", "Apply")}</Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map((i) => <div key={i} className="glass-card rounded-2xl h-80 skeleton" />)}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <p className="text-slate-400 mb-4">{t("لا توجد سيارات تطابق البحث", "No vehicles match")}</p>
            <Link to="/vehicles/post"><Button className="btn-primary">{t("كن أول من ينشر", "Be the first to post")}</Button></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v) => <VehicleCard key={v.vehicle_id} v={v} lang={language} t={t} onClick={() => navigate(`/vehicles/${v.vehicle_id}`)} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
